import React, { useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import './AttachmentPreview.scss';

const AttachmentPreview = ({ attachment, fileUrl, onClose }) => {
  const { t } = useLanguage();

  // Gestion des événements clavier et scroll
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (!attachment || !attachment.file) {
    return null;
  }

  const { file } = attachment;

  const isImage = () => {
    return file.mime_type && file.mime_type.startsWith('image/');
  };

  const isPDF = () => {
    return file.mime_type === 'application/pdf';
  };

  const handleBackdropClick = (e) => {
    // Fermer uniquement si on clique sur le backdrop
    if (e.target.classList.contains('attachment-preview-modal')) {
      onClose();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderContent = () => {
    if (isImage()) {
      // Ultra simple fullscreen image preview
      return (
        <div className="simple-image-preview">
          <button
            onClick={onClose}
            className="simple-close-btn"
            title={t('common.close')}
          >
            ✕
          </button>
          <img
            src={fileUrl}
            alt={file.filename}
            className="fullscreen-image"
            draggable={false}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    }

    if (isPDF()) {
      return (
        <div className="preview-pdf-container">
          <iframe
            src={fileUrl}
            className="preview-pdf"
            title={file.filename}
            frameBorder="0"
          />
        </div>
      );
    }

    return (
      <div className="preview-file-container">
        <div className="file-info">
          <div className="file-icon-large">📁</div>
          <h3 className="file-name">{file.filename}</h3>
          <p className="file-message">
            {t('attachments.previewNotAvailable')}
          </p>
          <button onClick={handleDownload} className="download-primary-btn">
            <span className="btn-icon">⬇️</span>
            {t('attachments.download')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="attachment-preview-modal" onClick={handleBackdropClick}>
      <div className="preview-container">
        {/* For images, show simple fullscreen preview */}
        {isImage() ? (
          renderContent()
        ) : (
          <>
            {/* Header avec infos et actions */}
            <div className="preview-header">
              <div className="file-info-header">
                <h2 className="file-title">{file.filename}</h2>
                {attachment.description && (
                  <p className="file-description">{attachment.description}</p>
                )}
                <div className="file-details">
                  <span className="file-size">
                    {file.file_size ? formatFileSize(file.file_size) : ''}
                  </span>
                  <span className="file-type">{file.mime_type}</span>
                </div>
              </div>

              <div className="preview-actions">
                <button
                  onClick={handleDownload}
                  className="action-button download-btn"
                  title={t('attachments.download')}
                >
                  <span className="btn-icon">⬇️</span>
                  <span className="btn-text">{t('attachments.download')}</span>
                </button>

                <button
                  onClick={onClose}
                  className="action-button close-btn"
                  title={t('common.close')}
                >
                  <span className="btn-icon">✕</span>
                  <span className="btn-text">{t('common.close')}</span>
                </button>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="preview-content">
              {renderContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );

  function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
};

export default AttachmentPreview;
